import { useEffect, useRef } from 'react'
import { marked } from 'marked'
import { MarkdownMessage } from '../lib/MarkdownMessage'
import { ScrollArea } from '../ui/scroll-area'
import { cn } from '../ui/utils'

export interface TranscriptMessage {
  id: string
  content: string
  sender: 'user' | 'ai'
  timestamp: Date
}

interface TranscriptDisplayProps {
  messages: TranscriptMessage[]
  currentChatName?: string
  className?: string
}

export function TranscriptDisplay({ messages, currentChatName, className }: TranscriptDisplayProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight
      }
    }
  }, [messages])

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {currentChatName && (
        <div className="px-4 py-3 border-b bg-background/50 backdrop-blur-sm">
          <h2 className="text-sm font-medium text-muted-foreground">{currentChatName}</h2>
        </div>
      )}
      <ScrollArea ref={scrollRef} className="flex-1">
        <div className="px-4 py-6 space-y-6">
          {messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              <p className="text-sm">Start speaking to begin the conversation</p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex flex-col gap-2",
                  message.sender === 'user' ? "items-end" : "items-start"
                )}
              >
                <div
                  className={cn(
                    "rounded-lg px-4 py-3 max-w-[80%]",
                    message.sender === 'user'
                      ? "bg-indigo-600 text-white"
                      : "bg-muted text-foreground"
                  )}
                >
                  {message.sender === 'ai' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <MarkdownMessage
                        content={message.content}
                        role="assistant"
                        className="text-sm"
                      />
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground px-2">
                  {formatTime(message.timestamp)}
                </span>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}


